<template>
  <div>
    <e-sidebar
      :title="isEdit ? $t('Editar Cartão') : $t('Novo Cartão')"
      :show="showSidebar"
      :fetching="fetching"
      :saving="saving"
      width="500px"
      @save="saveAdminCard"
      @hidden="showSidebar = false"
    >
      <template #content>
        <FormulateForm
          ref="adminCardForm"
          name="adminCardForm"
        >
          <b-row>
            <b-col md="12">
              <FormulateInput
                id="manage_admin_card_sidebar-card_number"
                v-model="localAdminCard.cardNumber"
                type="text"
                class="required"
                :label="$t('Número do cartão')"
                :instruction="$t('Formato hexadecimal (ex.: DC112A9A) ou use o botão Ler cartão')"
                validation="required|matches:/^[0-9A-Fa-f]{8}$/"
                :validation-messages="{
                  matches: $t('Informe 8 caracteres hexadecimais (ex.: DC112A9A)'),
                }"
                @input="onCardNumberInput"
              />
              <e-button
                class="mb-1"
                variant="outline-primary"
                icon="credit-card-2-front"
                :text="$t('Ler cartão')"
                @click="openReadCardModal"
              />
            </b-col>

            <b-col md="12">
              <e-user-search
                id="manage_admin_card_sidebar-user"
                v-model="localAdminCard.userId"
                class="required"
                :required="true"
                :label="$t('Usuário')"
                :default-options="userDefaultOptions"
              />
            </b-col>

            <b-col md="12">
              <e-store-combo
                id="manage_admin_card_sidebar-full_access_stores"
                v-model="localAdminCard.storeIds"
                :required="false"
                :multiple="true"
                :select-single-option="false"
                :label="$t('Lojas com acesso total (todas as SmartPos)')"
                :instruction="$t('O cartão acessa todas as SmartPos (atuais e futuras) das lojas selecionadas')"
                :placeholder="$t('Selecione')"
              />
            </b-col>

            <b-col md="12">
              <e-store-combo
                id="manage_admin_card_sidebar-store"
                v-model="storeFilter"
                :required="false"
                :select-single-option="false"
                :label="$t('Loja (filtro dos PDVs específicos)')"
                :placeholder="$t('Todas')"
              />
            </b-col>

            <b-col md="12">
              <FormulateInput
                id="manage_admin_card_sidebar-pay_boxes"
                v-model="localAdminCard.payBoxIds"
                type="vue-select"
                :multiple="true"
                :label="$t('Caixas (PDV) específicos autorizados')"
                :instruction="$t('Acesso pontual a PDVs específicos, além das lojas com acesso total')"
                :placeholder="$t('Selecione')"
                :options="filteredPayBoxesOptions"
              />
            </b-col>

            <b-col md="12">
              <FormulateInput
                id="manage_admin_card_sidebar-active"
                v-model="localAdminCard.active"
                type="switch"
                :label="$t('Ativo')"
              />
            </b-col>
          </b-row>
        </FormulateForm>
      </template>
    </e-sidebar>

    <b-modal
      id="readAdminCardModal"
      v-model="showReadCardModal"
      :title="$t('Ler cartão')"
      centered
      hide-footer
      no-close-on-backdrop
      @shown="focusReadInput"
    >
      <p>
        {{ $t('Aproxime o cartão do leitor. O código lido será convertido automaticamente.') }}
      </p>
      <b-form-input
        ref="readCardInput"
        v-model="readCardValue"
        :placeholder="$t('Aguardando leitura...')"
        autocomplete="off"
        @keydown.enter.prevent="applyReadCard"
      />
      <div class="d-flex justify-content-end mt-1">
        <e-button
          class="mr-1"
          variant="outline-primary"
          :text="$t('Cancelar')"
          @click="showReadCardModal = false"
        />
        <e-button
          variant="primary"
          :text="$t('Confirmar')"
          @click="applyReadCard"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BRow, BCol, BModal, BFormInput } from 'bootstrap-vue'
import { ESidebar } from '@/views/components'
import EButton from '@/views/components/EButton.vue'
import EUserSearch from '@/views/components/inputs/EUserSearch.vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import { decimalToHexUid, normalizeCardNumber } from '@/utils/card-number'
import { mapActions, mapGetters } from 'vuex'

const getInitialAdminCard = () => ({
  id: null,
  cardNumber: '',
  userId: null,
  active: true,
  payBoxIds: [],
  storeIds: [],
})

export default {
  components: {
    ESidebar,
    BRow,
    BCol,
    BModal,
    BFormInput,
    EButton,
    EUserSearch,
    EStoreCombo,
  },

  data() {
    return {
      showSidebar: false,
      fetching: false,
      saving: false,
      localAdminCard: getInitialAdminCard(),
      userDefaultOptions: [],
      showReadCardModal: false,
      readCardValue: '',
      storeFilter: null,
    }
  },

  computed: {
    ...mapGetters('pages/pdv/manageAdminCards', ['payBoxesOptions']),

    isEdit() {
      return !!this.localAdminCard?.id
    },

    // Filtra os PDVs pela loja selecionada, mas mantém sempre visíveis os já
    // selecionados (mesmo de outras lojas) para não perder o rótulo/seleção.
    filteredPayBoxesOptions() {
      if (!this.storeFilter) {
        return this.payBoxesOptions
      }

      const selectedIds = this.localAdminCard.payBoxIds || []

      return this.payBoxesOptions.filter(
        option =>
          String(option.storeId) === String(this.storeFilter) ||
          selectedIds.includes(option.value)
      )
    },
  },

  methods: {
    ...mapActions('pages/pdv/manageAdminCards', ['fetchAdminCardById', 'fetchPayBoxes']),

    // Ao digitar manualmente, remove separadores e aplica maiúsculas para manter o formato canônico.
    onCardNumberInput(value) {
      const cleaned = (value || '').replace(/[:\-\s]/g, '').toUpperCase()
      if (cleaned !== value) {
        this.localAdminCard.cardNumber = cleaned
      }
    },

    openReadCardModal() {
      this.readCardValue = ''
      this.showReadCardModal = true
    },

    focusReadInput() {
      this.$nextTick(() => {
        const input = this.$refs.readCardInput
        if (input) {
          input.focus()
        }
      })
    },

    // O leitor de cadastro envia o UID como decimal; convertemos explicitamente para hexadecimal.
    applyReadCard() {
      const raw = (this.readCardValue || '').toString().trim().replace(/[:\-\s]/g, '')

      if (!raw) {
        this.showError({ message: this.$t('Não foi possível ler o cartão. Tente novamente.') })
        return
      }

      const hex = /^\d+$/.test(raw) ? decimalToHexUid(Number(raw)) : normalizeCardNumber(raw)

      if (!/^[0-9A-F]{8}$/.test(hex)) {
        this.showError({ message: this.$t('Leitura inválida. Tente novamente.') })
        return
      }

      this.localAdminCard.cardNumber = hex
      this.showReadCardModal = false
    },

    showCreate() {
      this.localAdminCard = getInitialAdminCard()
      this.userDefaultOptions = []
      this.storeFilter = null
      this.fetchPayBoxes()
      this.showSidebar = true
    },

    async show(item) {
      this.storeFilter = null
      this.fetchPayBoxes()

      if (!item?.id) {
        this.showCreate()
        return
      }

      try {
        this.fetching = true
        this.showSidebar = true

        const data = await this.fetchAdminCardById(item.id)

        this.localAdminCard = {
          id: data.id,
          cardNumber: data.cardNumber,
          userId: data.userId,
          active: data.active,
          // vue-select compara os valores como string; convertemos os IDs recebidos.
          payBoxIds: (data.payBoxIds || []).map(String),
          storeIds: (data.storeIds || []).map(String),
        }

        // Garante que o usuário associado apareça no combo de busca
        if (data.userId) {
          this.userDefaultOptions = [
            { id: data.userId, name: data.userName || data.userId },
          ]
        }
      } catch (error) {
        this.showError({ error })
        this.showSidebar = false
      } finally {
        this.fetching = false
      }
    },

    async saveAdminCard() {
      this.$refs.adminCardForm.showErrors()
      if (this.$refs.adminCardForm.hasErrors) {
        this.showInvalidDataMessage()
        return
      }

      try {
        this.saving = true

        await this.$http({
          url: '/api/admin-cards',
          method: this.isEdit ? 'PUT' : 'POST',
          data: {
            id: this.localAdminCard.id,
            cardNumber: this.localAdminCard.cardNumber,
            userId: this.localAdminCard.userId,
            active: this.localAdminCard.active,
            // Backend espera List<int>; convertemos de volta os IDs string do vue-select.
            payBoxIds: (this.localAdminCard.payBoxIds || []).map(Number),
            storeIds: (this.localAdminCard.storeIds || []).map(Number),
          },
        })

        this.showSuccess({
          message: this.$t(`${this.isEdit ? 'Atualização' : 'Inclusão'} realizada com sucesso`),
        })
        this.showSidebar = false
        this.$emit('after-save')
      } catch (error) {
        this.showError({ error })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style></style>
